import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MARK_BOLD,
  MARK_ITALIC,
  MARK_UNDERLINE,
  MARK_STRIKETHROUGH,
  ELEMENT_OL,
  getPluginType,
  useEventPlateId,
  usePlateEditorRef,
} from '@udecode/plate';
import clsx from 'clsx';

import { ToolbarTooltipProvider } from './tooltip/Tooltip';
import ToolbarGroup from './ToolbarGroup';
import HeadingDropdown from '../dropdown/HeadingDropdown';
import FontDropdown from '../dropdown/FontDropdown';
import { FontSizeControl } from './font-size/FontSizeControl';
import { MarkToolbarButton } from '../button/MarkToolbarButton';
import { AlignmentDropdown } from '../dropdown/AlignmentDropdown';
import { HistoryButtons } from '../button/HistoryButtons';
import { IndentationButtons } from '../button/IndentationButtons';
import { ImageButton } from '../button/ImageButton';
import { ListToolbarButton } from '../button/ListToolbarButton';
import { LinkToolbarButton } from '../button/LinkToolbarButton';
import { MediaEmbedButton } from '../button/MediaEmbedButton';
import { ClearFormatButton } from '../button/ClearFormatButton';
import { TodoListButton } from '../button/TodoListButton';
import TableDropdown from '../dropdown/TableDropdown';
import ColorDropdown from '../dropdown/ColorDropdown';
import BgColorDropdown from '../dropdown/BgColorDropdown';
import { ListDropdown } from '../dropdown/ListDropdown';
import { HrButton } from '../button/HrButton';
import { PageBreakButton } from '../button/PageBreakButton';

import './toolbar.scss';

export const FieldEditorToolbar = ({ showImageModal, isSticky, toolbarProps, disableToolbarTab }) => {
  const { colorPickerProps } = toolbarProps || {};
  const editor = usePlateEditorRef(useEventPlateId());
  const { pluginsByKey } = editor || {};
  const {
    a,
    action_item,
    align,
    backgroundColor,
    bold,
    color,
    fontFamily,
    fontSize,
    heading,
    history,
    hr,
    img,
    indent,
    italic,
    list,
    media_embed,
    'page-break': pageBreak,
    resetNode,
    strikethrough,
    table,
    underline,
  } = pluginsByKey || {};

  if (!editor) return;

  return (
    <ToolbarTooltipProvider>
      <div className={clsx('plate-toolbar', isSticky && 'sticky')}>
        {history && (
          <ToolbarGroup>
            <HistoryButtons
              tabIndex={disableToolbarTab ? '-1' : '0'}
            />
          </ToolbarGroup>
        )}
        {indent && (
          <ToolbarGroup>
            <IndentationButtons
              tabIndex={disableToolbarTab ? '-1' : '0'}
            />
          </ToolbarGroup>
        )}
        {heading && (
          <ToolbarGroup>
            <HeadingDropdown
              tabIndex={disableToolbarTab ? '-1' : '0'}
            />
          </ToolbarGroup>
        )}
        {(fontSize || fontFamily || bold || italic || underline || strikethrough) && (
          <ToolbarGroup>
            {fontFamily && (
              <FontDropdown
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {fontSize && (
              <FontSizeControl
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {bold && (
              <MarkToolbarButton
                name="Bold"
                type={getPluginType(editor, MARK_BOLD)}
                icon={<FontAwesomeIcon icon={['fa', 'bold']} />}
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {italic && (
              <MarkToolbarButton
                name="Italic"
                type={getPluginType(editor, MARK_ITALIC)}
                icon={<FontAwesomeIcon icon={['fa', 'italic']} />}
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {underline && (
              <MarkToolbarButton
                name="Underline"
                type={getPluginType(editor, MARK_UNDERLINE)}
                icon={<FontAwesomeIcon icon={['fa', 'underline']} />}
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {strikethrough && (
              <MarkToolbarButton
                name="Strikethrough"
                type={getPluginType(editor, MARK_STRIKETHROUGH)}
                icon={<FontAwesomeIcon icon={['fa', 'strikethrough']} />}
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {align && (
              <AlignmentDropdown
                tabIndex={disableToolbarTab ? '-1': '0'}
              />
            )}
          </ToolbarGroup>
        )}
        {(color || backgroundColor) && (
          <ToolbarGroup>
            {color && (
              <ColorDropdown colorPickerProps={colorPickerProps} />
            )}
            {backgroundColor && (
              <BgColorDropdown colorPickerProps={colorPickerProps} />
            )}
          </ToolbarGroup>
        )}
        {(list || action_item) && (
          <ToolbarGroup>
            {list && (
              <>
                <ListDropdown
                  tabIndex={disableToolbarTab ? '-1' : '0'}
                />
                <ListToolbarButton
                  name="Numbered list"
                  type={ELEMENT_OL}
                  icon={<FontAwesomeIcon icon={['fa', 'list-ol']} />}
                  tabIndex={disableToolbarTab ? '-1' : '0'}
                />
              </>
            )}
            {action_item && (
              <TodoListButton
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
          </ToolbarGroup>
        )}
        {(a || img || media_embed || table) && (
          <ToolbarGroup>
            {a && (
              <LinkToolbarButton
                icon={<FontAwesomeIcon icon={['fa', 'link']} />}
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {img && (
              <ImageButton
                showImageModal={showImageModal}
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {media_embed && (
              <MediaEmbedButton
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {table && (
              <TableDropdown
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
          </ToolbarGroup>
        )}
        {(hr || resetNode || pageBreak) && (
          <ToolbarGroup>
            {hr && (
              <HrButton
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {resetNode && (
              <ClearFormatButton
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
            {pageBreak && (
              <PageBreakButton
                tabIndex={disableToolbarTab ? '-1' : '0'}
              />
            )}
          </ToolbarGroup>
        )}
      </div>
    </ToolbarTooltipProvider>
  );
};
